import { Box, IconButton, Typography, Button, Avatar, Badge, TextareaAutosize, CircularProgress } from '@mui/material';
import { ArrowBack, Notifications, Add, Send, Search } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import DesignSystem from '../../designSystem';
import { useAuth } from '../../contexts/AuthContext';
import { useT } from '../../i18n/useT';

const API_BASE = process.env.REACT_APP_API_URL || (typeof window !== 'undefined' ? window.location.origin : '');

interface HeaderProps {
  title?: string;
  showBackButton?: boolean;
  showSearch?: boolean;
  compact?: boolean;
  chatQuery?: string;
  onQueryChange?: (newValue: string) => void;
  onChatSubmit?: () => void;
  isChatLoading?: boolean;
  notificationBadgeCount?: number;
}

const defaultOnQueryChange = () => {};
const defaultOnChatSubmit = () => {};

export default function Header({
  title = 'VisiMe',
  showBackButton = false,
  showSearch = true,
  compact = false,
  chatQuery = '',
  onQueryChange = defaultOnQueryChange,
  onChatSubmit = defaultOnChatSubmit,
  isChatLoading = false,
  notificationBadgeCount,
}: HeaderProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const t = useT();

  const displayName =
    [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim() ||
    user?.email?.split('@')[0] ||
    t('myProfileAccountFallback');
  const avatarSrc =
    user?.profilePicture &&
    (user.profilePicture.startsWith('http')
      ? user.profilePicture
      : `${API_BASE}/assets/img/site/clients/img/${encodeURIComponent(user.profilePicture)}`);
  const initials =
    `${user?.firstName?.[0] ?? ''}${user?.lastName?.[0] ?? ''}`.trim() ||
    (user?.email?.[0]?.toUpperCase() ?? '?');

  const handleSubmit = () => onChatSubmit();

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSubmit();
    }
  };

  const showBadge =
    notificationBadgeCount !== undefined && notificationBadgeCount !== null && notificationBadgeCount > 0;

  return (
    <Box
      sx={{
        borderBottom: `1px solid ${DesignSystem.colors.border}`,
        position: 'sticky',
        top: 0,
        zIndex: 1100,
        backgroundColor: DesignSystem.mode === 'dark' ? 'rgba(0,0,0,0.82)' : 'rgba(242,242,247,0.88)',
        backdropFilter: 'blur(14px)',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          height: 52,
          gap: 1.25,
          px: { xs: 1.5, md: 2.5 },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
          {showBackButton && (
            <IconButton onClick={() => navigate(-1)} size="small" sx={{ color: DesignSystem.colors.textPrimary }}>
              <ArrowBack />
            </IconButton>
          )}
          <Typography
            sx={{
              color: DesignSystem.colors.primary,
              fontWeight: 700,
              letterSpacing: '-0.03em',
              cursor: 'pointer',
              fontSize: 18,
              display: { xs: 'block', md: compact ? 'none' : 'block' },
            }}
            onClick={() => navigate('/homepage')}
          >
            {title}
          </Typography>
        </Box>

        {showSearch ? (
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, justifyContent: 'center', minWidth: 0, px: 1 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                maxWidth: 480,
                border: `1px solid ${DesignSystem.colors.border}`,
                borderRadius: DesignSystem.borderRadius.md,
                px: 1.25,
                py: 0.25,
                backgroundColor: DesignSystem.colors.surface,
              }}
            >
              <TextareaAutosize
                minRows={1}
                maxRows={2}
                placeholder={t('headerAskProposals')}
                value={chatQuery}
                onChange={(e) => onQueryChange(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={isChatLoading}
                style={{
                  width: '100%',
                  border: 'none',
                  outline: 'none',
                  resize: 'none',
                  backgroundColor: 'transparent',
                  fontSize: 14,
                  fontFamily: 'inherit',
                  color: DesignSystem.colors.textPrimary,
                  paddingTop: 6,
                  paddingBottom: 6,
                }}
              />
              {isChatLoading ? (
                <CircularProgress size={20} sx={{ mx: 1, color: DesignSystem.colors.primary }} />
              ) : (
                <IconButton
                  onClick={handleSubmit}
                  disabled={!chatQuery.trim() || isChatLoading}
                  size="small"
                  sx={{ color: DesignSystem.colors.primary }}
                >
                  <Send fontSize="small" />
                </IconButton>
              )}
            </Box>
          </Box>
        ) : (
          <Box sx={{ flex: 1 }} />
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.35, flexShrink: 0 }}>
          {!showSearch && (
            <IconButton aria-label={t('headerSearchAria')} onClick={() => navigate('/search')} sx={{ color: DesignSystem.colors.textPrimary }}>
              <Search />
            </IconButton>
          )}
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate('/visi/create')}
            sx={{
              backgroundColor: DesignSystem.colors.action,
              color: DesignSystem.colors.onPrimary,
              px: 1.5,
              py: 0.55,
              // Desktop rail owns Create — avoid duplicate CTAs
              display: { xs: 'none', sm: compact ? 'none' : 'inline-flex', md: 'none' },
              borderRadius: DesignSystem.borderRadius.pill,
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: 'none',
              '&:hover': { backgroundColor: DesignSystem.colors.actionDark, boxShadow: 'none' },
            }}
          >
            {t('navCreate')}
          </Button>
          <IconButton
            aria-label={t('headerNotificationsAria')}
            sx={{ color: DesignSystem.colors.textPrimary }}
            onClick={() => navigate('/notifications')}
          >
            <Badge
              badgeContent={showBadge ? notificationBadgeCount : 0}
              invisible={!showBadge}
              sx={{ '& .MuiBadge-badge': { backgroundColor: DesignSystem.colors.error, color: DesignSystem.colors.onPrimary } }}
            >
              <Notifications />
            </Badge>
          </IconButton>
          <Box
            onClick={() => navigate('/profile')}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.85,
              cursor: 'pointer',
              borderRadius: DesignSystem.borderRadius.pill,
              pl: 0.25,
              pr: { md: 1 },
              py: 0.25,
              '&:hover': { backgroundColor: DesignSystem.colors.hover },
            }}
          >
            <Avatar
              src={avatarSrc || undefined}
              sx={{
                width: 30,
                height: 30,
                fontSize: 12,
                fontWeight: 700,
                bgcolor: DesignSystem.colors.surfaceElevated,
                color: DesignSystem.colors.textPrimary,
              }}
            >
              {!avatarSrc ? initials : null}
            </Avatar>
            <Typography
              sx={{
                display: { xs: 'none', lg: 'block' },
                fontSize: 13,
                fontWeight: 600,
                color: DesignSystem.colors.textPrimary,
                maxWidth: 130,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {user ? displayName : t('navGuest')}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
